import { useState, useEffect, useCallback } from 'react';
import { fmtMoney } from '@/lib/format';
import { FilterBar } from '@/components/dashboard/kit';
import { SectionHeader, LoadError } from '@/components/dashboard/MetricCard';
import { NetworkTreeNode } from '@/components/dashboard/NetworkTreeNode';
import NetworkTree from '@/components/dashboard/admin/NetworkTree';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '@/services/dashboardApi';
import { useDashboard } from '@/context/DashboardContext';
import { Users, UserPlus, TrendingUp, Loader2, Network, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

/*
  Red de afiliados (solo brokers Premium y equipo; ruta protegida con AccessGate y 403 en el servidor).
  El broker ve nombre, email y teléfono de su red hasta 3 niveles para poder asistirla (09/10/2026).
*/
function ContactNote() {
  return (
    <p className="flex items-start gap-2 text-[11px] text-gray-500 leading-relaxed mb-4" data-testid="network-contact-note">
      <ShieldCheck size={13} className="text-[#c9a84c]/70 mt-px shrink-0" aria-hidden="true" />
      Datos para ayudar a tu red dentro del club. Úsalos solo con ese fin (T&amp;C).
    </p>
  );
}

export default function DashboardAfiliados() {
  const { user } = useDashboard();
  const isAdmin = user?.role === 'admin';

  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(!isAdmin);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (isAdmin) return;
    setLoading(true);
    setError(null);
    Promise.all([dashboardApi.affiliates().then(setData), dashboardApi.myNetworkTree().then(setTree)])
      .catch((e) => { console.error(e); setError(e.message || 'Error'); }).finally(() => setLoading(false));
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="animate-spin text-[#c9a84c]" size={28} />
        <span className="text-xs text-gray-400">Cargando red...</span>
      </div>
    </div>
  );

  // ── EQUIPO: la red completa (la misma del panel admin › Personas › Red; la ficha se abre allí) ──
  if (isAdmin) {
    return (
      <div className="space-y-6" data-testid="dashboard-afiliados">
        <SectionHeader title="Red de afiliados" subtitle="Toda la red del club, todos los niveles. Pulsa un nombre para abrir su ficha." />
        <ContactNote />
        <FilterBar search={query} onSearch={setQuery} placeholder="Buscar en la red por nombre o email" />
        <NetworkTree query={query} onOpen={(key) => navigate(`/dashboard/admin?tab=personas&sub=red&id=${encodeURIComponent(key)}`)} />
      </div>
    );
  }

  if (error || !data) return (
    <div className="space-y-6" data-testid="dashboard-afiliados">
      <SectionHeader title="Red de afiliados" />
      <LoadError onRetry={load} />
    </div>
  );

  // ── BROKER VIEW — árbol jerárquico ──
  const totalMembers = tree?.total_members ?? data.total_members ?? 0;
  const activeCount = tree?.active_count ?? data.active_count ?? 0;
  const totalCommission = tree?.total_commission ?? data.total_commission ?? 0;
  const cur = 'eur'; // las comisiones van siempre en euros

  const statCards = [
    { icon: Users, label: 'Personas en tu red', value: totalMembers, color: '#c9a84c' },
    { icon: UserPlus, label: 'Activas', value: activeCount, color: '#10b981' },
    { icon: TrendingUp, label: 'Comisiones generadas', value: `${fmtMoney(totalCommission, cur)}`, color: '#c9a84c' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-afiliados">
      <SectionHeader
        title="Red de afiliados"
        subtitle="Tu red hasta 3 niveles: tus clientes directos y los de tus brokers, con su contacto. Pulsa + para desplegar cada rama."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.03 }}
            className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${s.color}10` }}>
              <s.icon size={16} style={{ color: s.color }} />
            </div>
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-gray-400 mt-1">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.25 }}
        className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-4">
          <div className="flex items-center gap-2">
            <Network size={14} className="text-[#c9a84c]" />
            <h3 className="text-white font-medium text-sm">Árbol de red</h3>
          </div>
          <span className="text-[11px] text-gray-400 uppercase tracking-wider font-medium">
            {tree?.source === 'cdm' ? 'Importe = lo que te ha generado cada persona' : 'Registros con tu enlace'}
          </span>
        </div>
        {tree?.root?.children?.length > 0 && <ContactNote />}

        {tree?.root && tree.root.children?.length > 0 ? (
          <NetworkTreeNode node={tree.root} depth={0} isRoot={true} showMetrics={true} currency={cur} />
        ) : (
          <div className="py-8 text-center">
            <p className="text-gray-400 text-sm">Aún no tienes afiliados en tu red</p>
            <p className="text-gray-500 text-xs mt-2">Comparte tu enlace de referido para empezar</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
